import { ArrowDown } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { renderMarkdownBlocks } from "@/lib/markdown";
import { resolveResourceUri } from "@/lib/resource-uri";
import type {
  ActionPreviewState,
  EntityRef,
  MaxwellMessage,
  MaxwellPart,
  RunStepProgress,
} from "@/types/maxwell";

import type { MaxwellBlock } from "@/types/blocks";

import { ActionPreviewCard } from "./ActionPreviewCard";
import { EntityLink } from "./EntityLink";
import { MaxwellToolChip } from "./MaxwellToolChip";
import { RunCheckpointCard } from "./RunCheckpointCard";
import { MaxwellBlocks } from "./blocks/MaxwellBlocks";
import { isEntityRef } from "./entity-refs";
import { useStickToBottom } from "./use-stick-to-bottom";

/**
 * Pull `entity_refs` citations out of a tool result's loose JSON, narrowing to
 * the `EntityRef` shape. Returns an empty array when the result carries none.
 */
function extractEntityRefs(result: unknown): EntityRef[] {
  if (typeof result !== "object" || result === null) return [];
  const refs = (result as { entity_refs?: unknown }).entity_refs;
  if (!Array.isArray(refs)) return [];
  return refs.filter(isEntityRef);
}

/**
 * Pull the content blocks out of a persisted tool result's loose JSON — the
 * reload-path mirror of the live `blocks` SSE frame. Returns an empty array
 * when the result carries none.
 */
function extractBlocks(result: unknown): MaxwellBlock[] {
  if (typeof result !== "object" || result === null) return [];
  const blocks = (result as { blocks?: unknown }).blocks;
  return Array.isArray(blocks) ? (blocks as MaxwellBlock[]) : [];
}

/**
 * Pull the `status_resource` (`northstar://agent_run/<uuid>`) off a
 * `start_agent_run` tool result, if present, and resolve it to a route.
 */
function runResourceHref(toolName: string, result: unknown): string | null {
  if (toolName !== "start_agent_run") return null;
  if (typeof result !== "object" || result === null) return null;
  const resource = (result as { status_resource?: unknown }).status_resource;
  if (typeof resource !== "string") return null;
  return resolveResourceUri(resource);
}

/** "View run" deep link, styled identically to `<EntityLink>` so it reads as
 *  one citation family with the entity-ref chips beside it. */
function ViewRunLink({ href }: { href: string }) {
  const navigate = useNavigate();
  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-auto p-0 text-sm text-primary hover:bg-transparent hover:underline"
      onClick={() => navigate(href)}
    >
      View run
    </Button>
  );
}

/**
 * The conversation transcript. User text is a right-aligned bubble; assistant
 * text is left-aligned prose; `tool_call`/`tool_result` parts render as
 * collapsible "shows its work" chips. `action_preview`/`checkpoint` parts are
 * defined but not yet rendered (Phases 3-4). While a reply streams, the
 * in-flight assistant text shows as a live bubble.
 */
export function MaxwellMessageList({
  messages,
  draftText,
  streaming,
  pendingPreview,
  onApprove,
  onReject,
  committing = false,
  activeRunIds = [],
  runProgress,
  onPromoteRun,
  resetKey,
}: {
  messages: MaxwellMessage[];
  draftText: string;
  streaming: boolean;
  /** Live write-confirmation card, shown at the transcript tail. */
  pendingPreview?: ActionPreviewState | null;
  onApprove?: () => void;
  onReject?: () => void;
  committing?: boolean;
  /** Background runs this thread started (`start_agent_run` tool results) —
   *  each renders a polling `RunCheckpointCard` at the transcript tail. */
  activeRunIds?: string[];
  /** Live per-step progress of in-chat-driven runs, keyed by run id. */
  runProgress?: Record<string, RunStepProgress[]>;
  /** Hand an in-chat run to the background worker ("Move to background"). */
  onPromoteRun?: (runId: string) => Promise<void>;
  /** Changing this (the active thread id) re-pins scroll and jumps to the
   *  bottom, so opening another conversation lands on its latest message. */
  resetKey?: string;
}) {
  const { setNode, onScroll, showJump, jumpToBottom } =
    useStickToBottom<HTMLDivElement>(resetKey);
  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div
        ref={setNode}
        onScroll={onScroll}
        className="scrollbar-thin flex flex-1 flex-col gap-3 overflow-y-auto px-4 py-3"
      >
        {messages.flatMap((m) =>
          m.parts.map((part, i) => renderPart(`${m.id}:${i}`, m.role, part)),
        )}

        {streaming && draftText && (
          <div className="flex max-w-[85%] flex-col gap-2 self-start rounded-lg bg-row-hover px-3 py-2 text-sm text-foreground">
            {renderMarkdownBlocks(draftText)}
          </div>
        )}

        {streaming && (
          <div className="flex items-center gap-2 self-start px-1 text-xs text-muted-foreground">
            <span aria-hidden className="h-2 w-2 animate-pulse rounded-full bg-primary" />
            Maxwell is working…
          </div>
        )}

        {pendingPreview && (
          <ActionPreviewCard
            summary={pendingPreview.summary}
            details={pendingPreview.details}
            status={pendingPreview.status}
            pending={committing}
            onApprove={onApprove ?? (() => {})}
            onReject={onReject ?? (() => {})}
          />
        )}

        {activeRunIds.map((runId) => (
          <RunCheckpointCard
            key={runId}
            runId={runId}
            progress={runProgress?.[runId]}
            onPromote={onPromoteRun}
          />
        ))}
      </div>

      {showJump && (
        <Button
          type="button"
          variant="subtle"
          size="sm"
          onClick={jumpToBottom}
          className="absolute bottom-3 left-1/2 -translate-x-1/2 gap-1.5 rounded-full shadow-md"
        >
          <ArrowDown size={14} />
          Jump to latest
        </Button>
      )}
    </div>
  );
}

function renderPart(key: string, role: string, part: MaxwellPart) {
  switch (part.kind) {
    case "text":
      if (!part.text) return null;
      if (role === "user") {
        return (
          <p
            key={key}
            className="max-w-[85%] self-end whitespace-pre-wrap rounded-lg bg-primary px-3 py-2 text-sm text-primary-foreground"
          >
            {part.text}
          </p>
        );
      }
      return (
        <div
          key={key}
          className="flex max-w-[85%] flex-col gap-2 self-start rounded-lg bg-row-hover px-3 py-2 text-sm text-foreground"
        >
          {renderMarkdownBlocks(part.text)}
        </div>
      );
    case "tool_call":
      return <MaxwellToolChip key={key} toolName={part.tool_name} payload={part.input} />;
    case "blocks":
      return <MaxwellBlocks key={key} blocks={part.blocks} />;
    case "tool_result": {
      const refs = extractEntityRefs(part.result);
      const runHref = runResourceHref(part.tool_name, part.result);
      const blocks = extractBlocks(part.result);
      return (
        <div key={key} className="flex flex-col gap-1.5">
          <MaxwellToolChip toolName={part.tool_name} payload={part.result} />
          {blocks.length > 0 && <MaxwellBlocks blocks={blocks} />}
          {(refs.length > 0 || runHref) && (
            <div className="flex flex-wrap gap-x-3 gap-y-1 pl-1">
              {refs.map((ref, j) => (
                <EntityLink key={`${ref.kind}:${ref.id}:${j}`} entity={ref} />
              ))}
              {runHref && <ViewRunLink href={runHref} />}
            </div>
          )}
        </div>
      );
    }
    case "run_results": {
      // The completed run's evidence, riding under the prose answer the runner
      // posted: the cited entities as deep-link chips, a link to the run in
      // Activity, and the per-step results behind a disclosure.
      const refs = Array.isArray(part.entity_refs)
        ? part.entity_refs.filter(isEntityRef)
        : [];
      return (
        <div key={key} className="flex flex-col gap-1.5">
          <MaxwellToolChip toolName="run_results" payload={part.steps} />
          <div className="flex flex-wrap gap-x-3 gap-y-1 pl-1">
            {refs.map((ref, j) => (
              <EntityLink key={`${ref.kind}:${ref.id}:${j}`} entity={ref} />
            ))}
            <EntityLink
              entity={{ kind: "agent_run", id: part.run_id, label: "View run" }}
            />
          </div>
        </div>
      );
    }
    // Preview/checkpoint parts: types exist, rendering is stubbed.
    case "action_preview":
    case "checkpoint":
      return null;
    default:
      return null;
  }
}
